import { useState } from 'react';
import type { DocumentReconstructedDocumentsListItem } from '@avisoma/shared';
import { BiDownload, BiShow } from 'react-icons/bi';
import { Link } from 'react-router-dom';
import { LazyImage } from './LazyImage';
import { FullScreenImagePreviewTrigger } from './FullScreenImagePreview';
import { PagePreviewHoverProvider } from './pagePreview/usePagePreviewHover';
import { PagePreviewHoverCard } from './pagePreview/PagePreviewHoverCard';
import { ShowMoreButton } from './ShowMoreButton';
import { Button } from '../catalyst/button';
import { pagePreviewSrc } from '../lib/pagePreviewSrc';

/**
 * Each card mounts a horizontal page-preview strip, so keep the preview's rendered window
 * independent from the shared geometry cache consumed by the map and chart.
 */
const INITIAL_VISIBLE_DOCS = 200;

export function DocumentReconstructionPreview({
  documentId,
  fileName,
  items,
  totalCount,
  hasMore,
  isLoading,
  isLoadingMore,
  error,
  isGenerationConflict,
  onLoadMore,
  onRetry,
}: {
  documentId: string;
  fileName: string;
  items: DocumentReconstructedDocumentsListItem[];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error?: Error;
  isGenerationConflict: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
}) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_DOCS);

  if (totalCount === 0 && !isLoading && !error) return null;

  const visibleItems = items.slice(0, visibleCount);
  const nextVisibleCount = Math.min(totalCount, visibleCount + INITIAL_VISIBLE_DOCS);
  const nextWindowIsCached = items.length >= nextVisibleCount;
  const previewHasMore = visibleItems.length < totalCount;
  const remaining = Math.max(0, totalCount - visibleItems.length);
  const loadMore = () => {
    if (!nextWindowIsCached && hasMore) onLoadMore();
    setVisibleCount(nextVisibleCount);
  };
  const errorMessage = isGenerationConflict
    ? 'The reconstruction changed while loading. Restart the list to load the current version.'
    : 'Could not load reconstructed documents.';

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2 px-1">
        <h2 className="text-sm font-medium text-gray-500 dark:text-zinc-400">Reconstructed Documents</h2>
        <span className="text-xs text-gray-500 dark:text-zinc-400">{totalCount} virtual docs</span>
      </div>

      <div className="bg-white dark:bg-zinc-800 dark:border dark:border-zinc-700 dark:shadow-zinc-950/30 shadow sm:rounded-lg overflow-hidden">
        <PagePreviewHoverProvider visibleIds={new Set(visibleItems.flatMap((item) => item.pageIds))}>
          <div className="max-h-[800px] overflow-y-auto bg-gray-50 dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-700">
            {isLoading && items.length === 0 && (
              <div className="p-4 text-sm text-gray-500 dark:text-zinc-400">Loading reconstructed documents…</div>
            )}

            {visibleItems.map((item, index) => (
              <div key={item.reconstructedDocumentId} className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/reconstructed-documents/${item.reconstructedDocumentId}`} className="group">
                    <div className="text-sm text-gray-900 dark:text-zinc-100 font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400">
                      Document {index + 1} ({item.pageIds.length} pages)
                    </div>
                    <div className="text-xs text-gray-500 dark:text-zinc-400">
                      <strong>Pages in upload:</strong> {item.startPageIndex + 1} - {item.endPageIndex + 1}
                    </div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-zinc-500 break-all">
                      <strong>ID:</strong> {item.reconstructedDocumentId}
                    </div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-zinc-500 break-all">
                      <strong>Suggested filename:</strong> {item.suggestedFilename}
                    </div>
                  </Link>

                  <div className="flex shrink-0 items-center gap-2">
                    <a
                      href={`/api/documents/${documentId}/download?${new URLSearchParams({
                        reconstructedDocumentId: item.reconstructedDocumentId,
                        inline: '1',
                      })}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-transparent text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-zinc-900"
                    >
                      <BiShow className="h-4 w-4" />
                      Preview
                    </a>
                    <a
                      href={`/api/documents/${documentId}/download?${new URLSearchParams({
                        reconstructedDocumentId: item.reconstructedDocumentId,
                      })}`}
                      className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-transparent text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-zinc-900"
                      download={reconstructedDownloadFileName(fileName, item.reconstructedDocumentId, item.suggestedFilename)}
                    >
                      <BiDownload className="h-4 w-4" />
                      Download
                    </a>
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap gap-1">
                  {item.classification && (
                    <span className="px-2 py-0.5 rounded whitespace-nowrap bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 text-xs">
                      {item.classification}
                      <span className="ml-1 opacity-80">({Math.round(item.classificationCertainty * 100)}% avg)</span>
                    </span>
                  )}
                  {item.sourceHints.title && (
                    <span
                      className="px-2 py-0.5 rounded truncate min-w-0 max-w-full bg-zinc-200 dark:bg-zinc-700 text-xs"
                      title={item.sourceHints.title}
                    >
                      {item.sourceHints.title}
                    </span>
                  )}
                  {item.sourceHints.esn && (
                    <span className="px-2 py-0.5 rounded whitespace-nowrap bg-amber-100 dark:bg-amber-900 text-xs">
                      ESN {item.sourceHints.esn}
                    </span>
                  )}
                  {item.sourceHints.tsn && (
                    <span className="px-2 py-0.5 rounded whitespace-nowrap bg-amber-100 dark:bg-amber-900 text-xs">
                      TSN {item.sourceHints.tsn}
                    </span>
                  )}
                  {item.sourceHints.csn && (
                    <span className="px-2 py-0.5 rounded whitespace-nowrap bg-amber-100 dark:bg-amber-900 text-xs">
                      CSN {item.sourceHints.csn}
                    </span>
                  )}
                </div>

                <div className="mt-3 max-h-[300px] overflow-x-auto overflow-y-hidden">
                  <div className="flex flex-row flex-nowrap gap-3 pb-1">
                    {item.pageIds.map((pageId, pageOrdinal) => (
                      <PagePreviewHoverCard
                        key={pageId}
                        page={{ id: pageId }}
                        className="shrink-0 flex flex-col gap-1 w-[150px]"
                      >
                        <div className="relative group bg-gray-200 dark:bg-zinc-700 rounded-md overflow-hidden shadow-[0_0_10px_rgba(0,0,0,0.1)]">
                          <div className="relative aspect-3/4 max-h-[280px]">
                            <LazyImage src={pagePreviewSrc(pageId, { resize: 'x400' })} className="w-full h-full object-cover" />
                            <FullScreenImagePreviewTrigger pageId={pageId} pageIds={item.pageIds} />
                          </div>
                        </div>
                        <Link
                          to={`/document-pages/${pageId}`}
                          className="text-xs text-center text-blue-600 dark:text-blue-400 hover:underline truncate"
                          title={pageId}
                        >
                          Page {pageOrdinal + 1}
                        </Link>
                      </PagePreviewHoverCard>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {error && (
              <div className="flex items-center justify-between gap-3 p-3 text-sm text-red-700 dark:text-red-300">
                <span>{errorMessage}</span>
                <Button plain onClick={onRetry} buttonClassName="shrink-0 font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                  {isGenerationConflict ? 'Restart list' : 'Retry'}
                </Button>
              </div>
            )}

            {previewHasMore && items.length > 0 && !isLoading && !error && (
              <ShowMoreButton
                remaining={remaining}
                total={totalCount}
                onClick={loadMore}
                loading={!nextWindowIsCached && isLoadingMore}
              />
            )}
          </div>
        </PagePreviewHoverProvider>
      </div>
    </div>
  );
}

function reconstructedDownloadFileName(
  originalFileName: string,
  reconstructedDocumentId: string,
  suggestedFilename: string | undefined,
) {
  const stem = originalFileName.replace(/\.[^/.]+$/, '') || 'document';
  const ext = suggestedFilename?.match(/\.[^/.]+$/)?.[0] ?? '.pdf';
  return `${stem}-reconstructed-${reconstructedDocumentId}${ext}`;
}
